/**
 * Story 2.5 — Correctietermijn bewaken (UX-DR14, AC-4/AC-5/AC-7).
 *
 * Puur presentatie-hulpje: formatteert de door de dienstlaag afgeleide
 * `correctieTermijnEinde`/`correctieTermijnVerstreken` (zie
 * `@functieloonhuis/shared`'s `workabilityResolver.ts`) naar de LETTERLIJKE
 * microcopy uit de UX-tabel. Geen eigen termijnrekenkunde -- die blijft
 * uitsluitend in de gedeelde pure functie (AD-14); dit bestand zet alleen
 * een datum om naar tekst.
 */

/** "31 augustus" — dag + volledige maandnaam, geen jaartal (UX-DR14). */
export function formatNlDagEnMaand(iso: string): string {
  const parts = iso.split('-').map(Number);
  const year = parts[0] ?? 1970;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** "Augustus" — volledige maandnaam met hoofdletter, voor de afgesloten-maand-waarschuwing (AC-4). */
export function formatNlMaandNaam(iso: string): string {
  const parts = iso.split('-').map(Number);
  const year = parts[0] ?? 1970;
  const month = parts[1] ?? 1;
  const date = new Date(Date.UTC(year, month - 1, 1));
  const naam = date.toLocaleDateString('nl-NL', { month: 'long', timeZone: 'UTC' });
  return naam.charAt(0).toUpperCase() + naam.slice(1);
}

/** Aantal hele dagen tussen `peildatum` en `termijnEinde` (kan negatief zijn ná de termijn). */
function dagenTot(termijnEinde: string, peildatum: string): number {
  const toUtcDays = (iso: string) => {
    const parts = iso.split('-').map(Number);
    return Date.UTC(parts[0] ?? 1970, (parts[1] ?? 1) - 1, parts[2] ?? 1) / 86_400_000;
  };
  return toUtcDays(termijnEinde) - toUtcDays(peildatum);
}

/**
 * AC-7: de slotregel bij een goedgekeurde verlofregel.
 * - Binnen de termijn, buiten de laatste drie dagen: "Nog aan te passen tot en met 31 augustus."
 * - Binnen de laatste drie dagen (0 t/m 3 dagen te gaan): "... — nog 2 dagen."
 * - Ná de termijn: "uren definitief geworden op 31 augustus"
 */
export function slotregelTekst(
  termijnEinde: string,
  termijnVerstreken: boolean,
  peildatum: string
): string {
  const datumTekst = formatNlDagEnMaand(termijnEinde);
  if (termijnVerstreken) {
    return `uren definitief geworden op ${datumTekst}`;
  }
  const dagenResterend = dagenTot(termijnEinde, peildatum);
  if (dagenResterend === 0) {
    // "nog 0 dagen" leest als een fout i.p.v. een dringende laatste kans --
    // op de einddatum zelf is dit letterlijk de laatste dag dat het nog kan.
    return `Nog aan te passen tot en met ${datumTekst} — dit is de laatste dag.`;
  }
  if (dagenResterend <= 3) {
    return `Nog aan te passen tot en met ${datumTekst} — nog ${dagenResterend} ${
      dagenResterend === 1 ? 'dag' : 'dagen'
    }.`;
  }
  return `Nog aan te passen tot en met ${datumTekst}.`;
}
